import { moneyToNumber } from "@babybjorn/utils/commercetools/money";
import type {
	Cart,
	Order,
	PaymentReference,
	TypedMoney,
} from "@commercetools/platform-sdk";
import type { ExpandedOrder } from "../../utils/commercetools/order-service";
import { formatPrice } from "./currency";
import { taxIsIncludedInPrice } from "./helpers";

/**
 * Returns a created TypedMoney based on the cent amount that is left to pay on the cart.
 * The amount can be negative if there is a gift card value that is higher than the total amount of the cart.
 */
export const getRemainingMoney = (cart: Cart | Order): TypedMoney => {
	const cartTotalMoney = cart.taxedPrice?.totalGross || cart.totalPrice;
	let existingAmount = cartTotalMoney.centAmount;
	const cartPayments = cart.paymentInfo?.payments || [];

	for (const payment of cartPayments) {
		const paymentMethod = payment.obj?.paymentMethodInfo.method;
		if (paymentMethod === "gift-card") {
			existingAmount -= payment.obj?.amountPlanned.centAmount || 0;
		} else {
			const transactions = payment.obj?.transactions || [];

			for (const transaction of transactions) {
				if (transaction.state === "Success") {
					if (
						transaction.type === "Charge" ||
						transaction.type === "Authorization"
					)
						existingAmount -= transaction.amount.centAmount;

					if (
						transaction.type === "Refund" ||
						transaction.type === "Chargeback"
					)
						existingAmount += transaction.amount.centAmount;
				}
			}
		}
	}

	return {
		...cartTotalMoney,
		centAmount: Math.max(existingAmount, 0),
	};
};

const getGiftCardPayments = (cart: Cart | Order): PaymentReference[] => {
	const payments = cart.paymentInfo?.payments || [];
	return payments.filter(
		(payment) =>
			payment.obj?.paymentMethodInfo.paymentInterface === "gift-card",
	);
};

export const getTotalCartPrice = (cart: Cart): TypedMoney => {
	return cart.taxedPrice?.totalGross || cart.totalPrice;
};

export const isCart = (obj: unknown): obj is Cart => {
	return !!(obj as Cart).cartState;
};

export type GetOrderSummaryPrices = ReturnType<typeof getOrderSummaryPrices>;

export const getOrderSummaryPrices = (cartOrOrder: Cart | ExpandedOrder) => {
	const products = cartOrOrder.lineItems.map<string>(
		(lineItem) =>
			`${lineItem.name[cartOrOrder.locale || ""]} x ${lineItem.quantity}`,
	);

	const totalMoney =
		cartOrOrder.taxedPrice?.totalGross || cartOrOrder.totalPrice;

	const shippingNetAmount = moneyToNumber(
		cartOrOrder.shippingInfo?.taxedPrice?.totalNet,
	);
	const totalTaxedNetAmount = cartOrOrder.taxedPrice
		? moneyToNumber(cartOrOrder.taxedPrice.totalNet)
		: moneyToNumber(cartOrOrder.totalPrice);

	const totalAmount = moneyToNumber(cartOrOrder.totalPrice);
	const shippingAmount = moneyToNumber(cartOrOrder.shippingInfo?.price);

	const subtotalTaxIncluded = Math.max(0, totalAmount - shippingAmount);

	let subtotalAmount = Math.max(0, totalTaxedNetAmount - shippingNetAmount);

	let totalTaxAmount = 0;
	for (const taxPortion of cartOrOrder.taxedPrice?.taxPortions || []) {
		totalTaxAmount += moneyToNumber(taxPortion.amount);
	}

	subtotalAmount = taxIsIncludedInPrice(cartOrOrder)
		? subtotalTaxIncluded
		: subtotalAmount;

	const subtotalFormatted = formatPrice(
		cartOrOrder.totalPrice.currencyCode,
		subtotalAmount,
	);

	const shippingFormatted = formatPrice(
		cartOrOrder.totalPrice.currencyCode,
		taxIsIncludedInPrice(cartOrOrder) ? shippingAmount : shippingNetAmount,
	);

	const giftCardPayments = getGiftCardPayments(cartOrOrder);

	let totalGiftCardAmount = 0;
	for (const giftCardPayment of giftCardPayments) {
		totalGiftCardAmount +=
			moneyToNumber(giftCardPayment.obj?.amountPlanned) || 0;
	}

	const totalExcludingGiftcard = Math.max(
		moneyToNumber(totalMoney) - totalGiftCardAmount,
		0,
	);

	const totalFormatted = formatPrice(
		totalMoney.currencyCode,
		totalExcludingGiftcard,
	);

	const taxIsIncluded = taxIsIncludedInPrice(cartOrOrder);

	return {
		products,
		totalFormatted,
		totalExcludingGiftcard,
		shippingFormatted,
		shippingAmount,
		subtotalFormatted,
		subtotalAmount,
		taxIsIncluded,
		totalTaxAmount,
	};
};
